import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from 'react-query';
import {
  Container,
  Typography,
  Box,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  IconButton,
  TextField,
  Dialog,
  DialogTitle,
  DialogContent,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Alert,
  CircularProgress,
  Tooltip,
} from '@mui/material';
import {
  Add,
  Edit,
  Delete,
  Search,
  Refresh,
  VpnKey,
} from '@mui/icons-material';
import { usersApi } from '../services/api';
import { User, UserCreate, UserUpdate } from '../types';

const Users: React.FC = () => {
  const [searchTerm, setSearchTerm] = useState('');
  const [roleFilter, setRoleFilter] = useState<'all' | 'user' | 'manager'>('all');
  const [activeFilter, setActiveFilter] = useState<'all' | 'active' | 'inactive'>('all');
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [passwordResetDialogOpen, setPasswordResetDialogOpen] = useState(false);
  const [selectedUser, setSelectedUser] = useState<User | null>(null);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  const queryClient = useQueryClient();

  const { data: usersData, isLoading, refetch } = useQuery(
    ['users', searchTerm, roleFilter, activeFilter],
    () => usersApi.getUsers({
      search: searchTerm || undefined,
      role: roleFilter !== 'all' ? roleFilter : undefined,
      is_active: activeFilter === 'all' ? undefined : activeFilter === 'active',
    }),
    {
      onError: (error: any) => {
        setError(error.response?.data?.detail || 'Failed to fetch users');
      }
    }
  );

  const createUserMutation = useMutation(usersApi.createUser, {
    onSuccess: () => {
      queryClient.invalidateQueries(['users']);
      setCreateDialogOpen(false);
      setError('');
    },
    onError: (error: any) => {
      setError(error.response?.data?.detail || 'Failed to create user');
    }
  });

  const updateUserMutation = useMutation(
    ({ userId, data }: { userId: number; data: UserUpdate }) =>
      usersApi.updateUser(userId, data),
    {
      onSuccess: () => {
        queryClient.invalidateQueries(['users']);
        setEditDialogOpen(false);
        setSelectedUser(null);
        setError('');
      },
      onError: (error: any) => {
        setError(error.response?.data?.detail || 'Failed to update user');
      }
    }
  );

  const deleteUserMutation = useMutation(usersApi.deleteUser, {
    onSuccess: () => {
      queryClient.invalidateQueries(['users']);
      setError('');
    },
    onError: (error: any) => {
      setError(error.response?.data?.detail || 'Failed to delete user');
    }
  });

  const resetPasswordMutation = useMutation(
    ({ userId, password }: { userId: number; password: string }) =>
      usersApi.resetUserPassword(userId, { new_password: password }),
    {
      onSuccess: () => {
        setPasswordResetDialogOpen(false);
        setSelectedUser(null);
        setSuccess('Password reset successfully');
        setError('');
      },
      onError: (error: any) => {
        setError(error.response?.data?.detail || 'Failed to reset password');
        setSuccess('');
      }
    }
  );

  const handleCreateUser = (userData: UserCreate | UserUpdate) => {
    createUserMutation.mutate(userData as UserCreate);
  };

  const handleUpdateUser = (userData: UserUpdate) => {
    if (selectedUser) {
      updateUserMutation.mutate({ userId: selectedUser.id, data: userData });
    }
  };

  const handleDeleteUser = (userId: number) => {
    if (window.confirm('Are you sure you want to delete this user? This action cannot be undone.')) {
      deleteUserMutation.mutate(userId);
    }
  };

  const PasswordResetDialog: React.FC<{
    user: User | null;
    open: boolean;
    onClose: () => void;
  }> = ({ user, open, onClose }) => {
    const [newPassword, setNewPassword] = useState('');
    const [confirmPassword, setConfirmPassword] = useState('');
    const [localError, setLocalError] = useState('');

    const handleSubmit = (e: React.FormEvent) => {
      e.preventDefault();

      if (newPassword.length < 8) {
        setLocalError('Password must be at least 8 characters long');
        return;
      }

      if (newPassword !== confirmPassword) {
        setLocalError('Passwords do not match');
        return;
      }

      if (user) {
        resetPasswordMutation.mutate({ userId: user.id, password: newPassword });
      }
    };

    const handleClose = () => {
      setNewPassword('');
      setConfirmPassword('');
      setLocalError('');
      onClose();
    };

    return (
      <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
        <DialogTitle>Reset Password for {user?.first_name} {user?.last_name}</DialogTitle>
        <DialogContent>
          {localError && (
            <Alert severity="error" sx={{ mb: 2 }} onClose={() => setLocalError('')}>
              {localError}
            </Alert>
          )}
          <Box component="form" onSubmit={handleSubmit}>
            <TextField
              fullWidth
              label="New Password"
              type="password"
              value={newPassword}
              onChange={(e) => setNewPassword(e.target.value)}
              margin="normal"
              required
              helperText="Must be at least 8 characters long"
            />
            <TextField
              fullWidth
              label="Confirm New Password"
              type="password"
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              margin="normal"
              required
            />
            <Box sx={{ mt: 2, display: 'flex', gap: 2 }}>
              <Button
                type="submit"
                variant="contained"
                disabled={resetPasswordMutation.isLoading}
                fullWidth
              >
                {resetPasswordMutation.isLoading ? <CircularProgress size={24} /> : 'Reset Password'}
              </Button>
              <Button
                variant="outlined"
                onClick={handleClose}
                disabled={resetPasswordMutation.isLoading}
                fullWidth
              >
                Cancel
              </Button>
            </Box>
          </Box>
        </DialogContent>
      </Dialog>
    );
  };

  const UserForm: React.FC<{
    user?: User;
    onSubmit: (data: UserCreate | UserUpdate) => void;
    onCancel: () => void;
    loading: boolean;
  }> = ({ user, onSubmit, onCancel, loading }) => {
    const [formData, setFormData] = useState({
      email: user?.email || '',
      username: user?.username || '',
      password: '',
      first_name: user?.first_name || '',
      last_name: user?.last_name || '',
      phone: user?.phone || '',
      employee_id: user?.employee_id || '',
      department: user?.department || '',
      role: user?.role || 'user' as 'user' | 'manager',
      is_active: user?.is_active !== undefined ? user.is_active : true,
    });

    const handleSubmit = (e: React.FormEvent) => {
      e.preventDefault();
      const submitData = user
        ? { ...formData, password: undefined } // Don't include password for updates
        : formData;
      onSubmit(submitData as any);
    };

    return (
      <Box component="form" onSubmit={handleSubmit}>
        <TextField
          fullWidth
          label="Email"
          type="email"
          value={formData.email}
          onChange={(e) => setFormData({ ...formData, email: e.target.value })}
          margin="normal"
          required
        />
        <TextField
          fullWidth
          label="Username"
          value={formData.username}
          onChange={(e) => setFormData({ ...formData, username: e.target.value })}
          margin="normal"
          required
        />
        {!user && (
          <TextField
            fullWidth
            label="Password"
            type="password"
            value={formData.password}
            onChange={(e) => setFormData({ ...formData, password: e.target.value })}
            margin="normal"
            required
          />
        )}
        <TextField
          fullWidth
          label="First Name"
          value={formData.first_name}
          onChange={(e) => setFormData({ ...formData, first_name: e.target.value })}
          margin="normal"
          required
        />
        <TextField
          fullWidth
          label="Last Name"
          value={formData.last_name}
          onChange={(e) => setFormData({ ...formData, last_name: e.target.value })}
          margin="normal"
          required
        />
        <TextField
          fullWidth
          label="Phone"
          value={formData.phone}
          onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
          margin="normal"
        />
        <TextField
          fullWidth
          label="Employee ID"
          value={formData.employee_id}
          onChange={(e) => setFormData({ ...formData, employee_id: e.target.value })}
          margin="normal"
        />
        <TextField
          fullWidth
          label="Department"
          value={formData.department}
          onChange={(e) => setFormData({ ...formData, department: e.target.value })}
          margin="normal"
        />
        <FormControl fullWidth margin="normal">
          <InputLabel id="role-label">Role</InputLabel>
          <Select
            labelId="role-label"
            id="role-select"
            value={formData.role}
            label="Role"
            onChange={(e) => setFormData({ ...formData, role: e.target.value as 'user' | 'manager' })}
          >
            <MenuItem value="user">User</MenuItem>
            <MenuItem value="manager">Manager</MenuItem>
          </Select>
        </FormControl>
        {user && (
          <FormControl fullWidth margin="normal">
            <InputLabel>Status</InputLabel>
            <Select
              value={formData.is_active ? 'active' : 'inactive'}
              onChange={(e) => setFormData({ ...formData, is_active: e.target.value === 'active' })}
            >
              <MenuItem value="active">Active</MenuItem>
              <MenuItem value="inactive">Inactive</MenuItem>
            </Select>
          </FormControl>
        )}
        <Box sx={{ mt: 2, display: 'flex', gap: 2 }}>
          <Button
            type="submit"
            variant="contained"
            disabled={loading}
            fullWidth
          >
            {loading ? <CircularProgress size={24} /> : (user ? 'Update' : 'Create')}
          </Button>
          <Button
            variant="outlined"
            onClick={onCancel}
            disabled={loading}
            fullWidth
          >
            Cancel
          </Button>
        </Box>
      </Box>
    );
  };

  return (
    <Container maxWidth="lg">
      <Box sx={{ mb: 4 }}>
        <Typography variant="h4" component="h1" gutterBottom>
          User Management
        </Typography>

        {error && (
          <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>
            {error}
          </Alert>
        )}

        {success && (
          <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess('')}>
            {success}
          </Alert>
        )}

        <Box sx={{ display: 'flex', gap: 2, mb: 3, flexWrap: 'wrap' }}>
          <TextField
            label="Search users..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            InputProps={{
              startAdornment: <Search sx={{ mr: 1, color: 'text.secondary' }} />,
            }}
            sx={{ minWidth: 200 }}
          />
          <FormControl sx={{ minWidth: 120 }}>
            <InputLabel>Role</InputLabel>
            <Select
              value={roleFilter}
              onChange={(e) => setRoleFilter(e.target.value as any)}
            >
              <MenuItem value="all">All Roles</MenuItem>
              <MenuItem value="user">Users</MenuItem>
              <MenuItem value="manager">Managers</MenuItem>
            </Select>
          </FormControl>
          <FormControl sx={{ minWidth: 120 }}>
            <InputLabel>Status</InputLabel>
            <Select
              value={activeFilter}
              onChange={(e) => setActiveFilter(e.target.value as any)}
            >
              <MenuItem value="all">All Status</MenuItem>
              <MenuItem value="active">Active</MenuItem>
              <MenuItem value="inactive">Inactive</MenuItem>
            </Select>
          </FormControl>
          <Button
            variant="contained"
            startIcon={<Add />}
            onClick={() => setCreateDialogOpen(true)}
          >
            Add User
          </Button>
          <IconButton onClick={() => refetch()}>
            <Refresh />
          </IconButton>
        </Box>

        {isLoading ? (
          <Box display="flex" justifyContent="center" p={4}>
            <CircularProgress />
          </Box>
        ) : (
          <TableContainer component={Paper}>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Name</TableCell>
                  <TableCell>Email</TableCell>
                  <TableCell>Username</TableCell>
                  <TableCell>Role</TableCell>
                  <TableCell>Department</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell>Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {usersData?.users?.map((user: User) => (
                  <TableRow key={user.id}>
                    <TableCell>
                      {user.first_name} {user.last_name}
                      {user.employee_id && (
                        <Typography variant="caption" display="block" color="textSecondary">
                          ID: {user.employee_id}
                        </Typography>
                      )}
                    </TableCell>
                    <TableCell>{user.email}</TableCell>
                    <TableCell>{user.username}</TableCell>
                    <TableCell>
                      <Chip
                        label={user.role}
                        color={user.role === 'manager' ? 'primary' : 'default'}
                        size="small"
                      />
                    </TableCell>
                    <TableCell>{user.department || '-'}</TableCell>
                    <TableCell>
                      <Chip
                        label={user.is_active ? 'Active' : 'Inactive'}
                        color={user.is_active ? 'success' : 'error'}
                        size="small"
                      />
                    </TableCell>
                    <TableCell>
                      <Tooltip title="Edit">
                        <IconButton
                          onClick={() => {
                            setSelectedUser(user);
                            setEditDialogOpen(true);
                          }}
                        >
                          <Edit />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Reset Password">
                        <IconButton
                          onClick={() => {
                            setSelectedUser(user);
                            setPasswordResetDialogOpen(true);
                          }}
                          color="warning"
                        >
                          <VpnKey />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Delete">
                        <IconButton
                          onClick={() => handleDeleteUser(user.id)}
                          color="error"
                        >
                          <Delete />
                        </IconButton>
                      </Tooltip>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </Box>

      {/* Create User Dialog */}
      <Dialog open={createDialogOpen} onClose={() => setCreateDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Create New User</DialogTitle>
        <DialogContent>
          <UserForm
            onSubmit={handleCreateUser}
            onCancel={() => setCreateDialogOpen(false)}
            loading={createUserMutation.isLoading}
          />
        </DialogContent>
      </Dialog>

      {/* Edit User Dialog */}
      <Dialog open={editDialogOpen} onClose={() => setEditDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Edit User</DialogTitle>
        <DialogContent>
          {selectedUser && (
            <UserForm
              user={selectedUser}
              onSubmit={handleUpdateUser}
              onCancel={() => {
                setEditDialogOpen(false);
                setSelectedUser(null);
              }}
              loading={updateUserMutation.isLoading}
            />
          )}
        </DialogContent>
      </Dialog>

      {/* Password Reset Dialog */}
      <PasswordResetDialog
        user={selectedUser}
        open={passwordResetDialogOpen}
        onClose={() => {
          setPasswordResetDialogOpen(false);
          setSelectedUser(null);
        }}
      />
    </Container>
  );
};

export default Users;